import React, { createContext, useContext, useEffect, useState } from 'react'
import { AuthContextType, User, Subscription, QuotaStatus } from '../types'
import apiService from './api'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatus | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshAuth = async () => {
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        setLoading(false)
        return
      }

      const [userData, subscriptionData, quotaData] = await Promise.allSettled([
        apiService.getProfile(),
        apiService.getSubscription(),
        apiService.getQuotaStatus()
      ])

      if (userData.status === 'fulfilled') {
        setUser(userData.value)
      }

      if (subscriptionData.status === 'fulfilled') {
        setSubscription(subscriptionData.value)
      }

      if (quotaData.status === 'fulfilled') {
        setQuotaStatus(quotaData.value)
      }
    } catch (error) {
      console.error('Auth refresh failed:', error)
      localStorage.removeItem('authToken')
      localStorage.removeItem('refreshToken')
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const { user: userData, accessToken, refreshToken } = await apiService.login(email, password)
      
      localStorage.setItem('authToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      
      setUser(userData)
      
      // Fetch subscription and quota data
      const [subscriptionData, quotaData] = await Promise.allSettled([
        apiService.getSubscription(),
        apiService.getQuotaStatus()
      ])

      if (subscriptionData.status === 'fulfilled') {
        setSubscription(subscriptionData.value)
      }

      if (quotaData.status === 'fulfilled') {
        setQuotaStatus(quotaData.value)
      }
    } catch (error) {
      throw error
    }
  }

  const signup = async (email: string, password: string) => {
    try {
      const { user: userData, accessToken, refreshToken } = await apiService.signup(email, password)
      
      localStorage.setItem('authToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      
      setUser(userData)
      
      // Fetch subscription and quota data
      const [subscriptionData, quotaData] = await Promise.allSettled([
        apiService.getSubscription(),
        apiService.getQuotaStatus()
      ])

      if (subscriptionData.status === 'fulfilled') {
        setSubscription(subscriptionData.value)
      }

      if (quotaData.status === 'fulfilled') {
        setQuotaStatus(quotaData.value)
      }
    } catch (error) {
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    setUser(null)
    setSubscription(null)
    setQuotaStatus(null)
    
    // Call logout endpoint
    apiService.logout().catch(console.error)
  }

  useEffect(() => {
    // Check for OAuth callback tokens in URL
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    const refreshToken = urlParams.get('refresh_token')
    
    if (token) {
      // Store token from OAuth callback (refreshToken is optional for Premiere clients)
      localStorage.setItem('authToken', token)
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
      }
      
      // Clean up URL (only if we're on a page that should handle it)
      // Don't clean up if we're on /subscribe - let SubscribePage handle it
      if (window.location.pathname !== '/subscribe') {
        urlParams.delete('token')
        urlParams.delete('refresh_token')
        const cleanUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '')
        window.history.replaceState({}, document.title, cleanUrl)
      }
      
      // Refresh auth to get user data
      refreshAuth()
    } else {
      // Only refresh auth if we have a token in localStorage
      const storedToken = localStorage.getItem('authToken')
      if (storedToken) {
        refreshAuth()
      } else {
        setLoading(false)
      }
    }
  }, [])

  const value: AuthContextType = {
    user,
    subscription,
    quotaStatus,
    login,
    signup,
    logout,
    refreshAuth,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
